/**
 * Preview math, mirrors server BigDecimal.
 *
 * This controller computes amount previews CLIENT-SIDE (rounded to 2 decimals
 * via Math.round on integer-cent values) for live UX feedback while the
 * architect tweaks line inputs. The server STILL re-computes canonically via
 * `MoeFee::Computer` with `BigDecimal#round(2, ROUND_HALF_UP)` on save —
 * client output is only displayed, never persisted.
 *
 * Do NOT add business rules or validation here — they belong in
 * `app/services/moe_fee/computer.rb`. This controller is for visual feedback
 * only; treat its math as approximate (it agrees with the server by
 * construction for non-negative integer-cent inputs, but new business rules
 * must land server-side first).
 *
 * Mirrors: `app/services/moe_fee/computer.rb` (canonical money math).
 */
import { Controller } from '@hotwired/stimulus';

/**
 * MoeFee Assiette Controller
 *
 * Per-line interactivity for a `MoeFeeLine` row inside the MoeFee edit form.
 * Mounted by `MoeFee::LineRowComponent` on each row in the sortable list.
 *
 * Responsibilities:
 * - Switch the row UI between "forfait" (fixed amount) and "variable" (percentage of an
 *   assiette) when the kind segmented control changes.
 * - Show/hide the "Assiette de cette ligne" exclusions panel — only relevant when
 *   the line is variable.
 * - Sync the paired hidden input's `disabled` attribute when a contract or
 *   additional-work exclusion checkbox is toggled, so only excluded items are
 *   submitted (browsers omit disabled controls from form submission).
 * - Soft-delete the entire row by setting the row-level `_destroy` field and
 *   visually hiding the element (without removing from the DOM, so the hidden
 *   field is still submitted).
 * - Live-recompute the assiette panel summary (`<assiette> × <rate> % = <HT>`)
 *   and the exclusions badge whenever the user toggles checkboxes, types a
 *   percentage, or switches kind. Integer-cent math matches server
 *   BigDecimal#round(2, ROUND_HALF_UP) by construction (no float drift).
 * - React to the global TVA segmented in the parent edit form via the
 *   `moe-fee-global-vat:changed` window event broadcast by
 *   `MoeFeeGlobalVatController` — updates this row's per-line hint without
 *   a server round-trip.
 *
 * Targets:
 * - kindRadio: Each radio input in the kind segmented control
 *   (values: "forfait" | "variable")
 * - forfaitField: Wrapper element for the fixed-amount input (shown when kind=forfait)
 * - forfaitInput: The numeric `fixed_amount_ht` input (cleared when switching away)
 * - variableField: Wrapper for the percentage-rate input (shown when kind=variable)
 * - variableInput: The numeric `percentage_rate` input (cleared when switching away)
 * - assietteSection: Wrapper for the per-line exclusions panel
 *   (visible only when kind=variable)
 * - contractCheckbox: One checkbox per Contract that *can* be excluded from this
 *   line's assiette. Checked = INCLUDED in the assiette.
 *   Required `data-contract-id` attribute carries the Contract id (matched
 *   against `contractExclusionId`'s `data-contract-id` to pair them).
 *   Required `data-amount-ht-cents` attribute carries the contract's
 *   base_amount_ht in INTEGER cents — fed into the live recompute sum.
 * - contractExclusionId: The paired hidden input carrying `value="<contract.id>"`.
 *   Starts `disabled` when the contract is INCLUDED (browsers omit disabled
 *   controls from submission, so the submitted set is the excluded set).
 *   Required `data-contract-id` matches the checkbox.
 * - additionalWorkCheckbox: Same as `contractCheckbox` but for AdditionalWork.
 *   Pairs via `data-additional-work-id`.
 * - additionalWorkExclusionId: Same as `contractExclusionId` but for AdditionalWork.
 * - destroyField: Hidden `_destroy` input for the line itself
 *   (toggled by `removeRow`).
 * - summaryAssiette: Live-formula <strong> for the post-exclusion assiette
 *   in the panel summary (left of the `×`).
 * - summaryRate: Live-formula <strong> for the rate (between `×` and `%`).
 * - summaryHt: Live-formula <strong> for the computed HT (right of the `=`).
 * - exclusionsBadge: Wrapper around the exclusions warning badge in the
 *   panel summary; toggled `.hidden` based on count.
 * - globalVatHint: <p> below the per-line VAT select that mirrors the
 *   form-level `MoeFee#vat_rate` ("TVA par défaut : X %").
 *   Updated by `globalVatChanged` in response to the broadcaster event.
 *
 * Values:
 * - assiette (Number, default 0): Project's gross assiette in € — sum of all
 *   eligible contracts + AWs at server render time. Set by the ERB on the
 *   row element. Currently informational; `#recomputeVariable` derives the
 *   line's effective assiette from currently-checked checkboxes (in cents)
 *   so the live formula reacts to user toggles. Kept for future "show the
 *   project gross above the formula" UI.
 *
 * Actions:
 * - kindChanged: Triggered by `change` on a kindRadio. Toggles field/section
 *   visibility, clears the now-inactive numeric input, backfills the now-active
 *   input to '0' if empty, then recomputes.
 * - toggleContract: Triggered by `change` on a contractCheckbox. Toggles the
 *   `disabled` attribute on the paired `contractExclusionId` hidden, then recomputes.
 * - toggleAdditionalWork: Same as `toggleContract` for AW exclusions.
 * - removeRow: Marks the entire line for destruction, hides it and disables its
 *   fields except `_destroy` and the row `id` (see `#retireFields`).
 * - recompute: Triggered by `input` on the percentage / fixed-amount inputs
 *   (only the percentage feeds the live formula in v3 — forfait has no
 *   summary slot to update).
 * - globalVatChanged: Listens for the `moe-fee-global-vat:changed` window
 *   event fired by MoeFeeGlobalVatController; updates the per-line VAT hint.
 */
export default class extends Controller {
  static targets = [
    'kindRadio',
    'forfaitField',
    'forfaitInput',
    'variableField',
    'variableInput',
    // Extras blocks below the [montant | TVA] grid row: forfait's "Répartir" +
    // breakdown, variable's "Répartition de la TVA" segmented + preview. Their
    // visibility follows the kind alongside the amount field.
    'forfaitExtrasField',
    'variableExtrasField',
    // Always-visible helper under "Montant HT" shown only when the value is a
    // derived total (multi-rate split ON).
    'forfaitDerivedHint',
    'assietteSection',
    'contractCheckbox',
    'contractExclusionId',
    'additionalWorkCheckbox',
    'additionalWorkExclusionId',
    'destroyField',
    'summaryAssiette',
    'summaryRate',
    'summaryHt',
    'exclusionsBadge',
    'globalVatHint',
    // Accordion summary header (T13/B2 — live-mirror the editing state).
    'headerLabel',
    'headerForfaitBadge',
    'headerVariableBadge',
    'headerAmount',
    // Per-contract "X/Y TS inclus" counter shown on each marché's accordion
    // summary. Updated live by #refreshTsCountersForContract whenever a TS
    // checkbox inside that marché is toggled. Carries `data-contract-id`.
    'contractTsCounter',
    // ── Multi-TVA v2 (spec §5.4) ──
    // splitVatCheckbox: the "Répartir sur plusieurs taux de TVA" checkbox; ON
    //   reveals the breakdown block + flips `forfaitInput` to read-only.
    // breakdownBlock: wrapper around the 3 fixed-€ breakdown inputs (hidden
    //   when split is OFF).
    // breakdownInput: each of the 3 fixed-€ inputs (5,5 / 10 / 20 %).
    // breakdownTotal: the "Total réparti" readout (Σ of the 3 inputs).
    // weightChantierCheckbox: the variable "Répartir la TVA selon les marchés
    //   du chantier" checkbox; ON = pondération chantier, OFF = single-rate via
    //   the shared VAT select.
    // weightedPreview: the read-only weighted-VAT preview block (visible only
    //   when chantier-weighting is ON); content is server-rendered from the
    //   parent's memoized Computer result — JS only toggles its visibility.
    // singleVatField: the SHARED mono-rate VAT select; visible only in mono
    //   mode ((forfait && !split) || (variable && !weighted)).
    'splitVatCheckbox',
    'breakdownBlock',
    'breakdownInput',
    'breakdownTotal',
    'weightChantierCheckbox',
    'weightedPreview',
    // weightedPreviewRows: the inner container the controller (re)fills with
    //   one row per VAT rate present in the included marchés' buckets. Rendered
    //   CLIENT-SIDE so the preview is live even for an unsaved line.
    'weightedPreviewRows',
    'singleVatField',
  ];

  static values = {
    // Project's gross assiette in € (Number — safe for amounts up to 2^53 cents).
    // Falls back to 0 so unsaved rows that haven't been hydrated don't crash.
    assiette: { type: Number, default: 0 },
  };

  connect() {
    // A row re-rendered as removed (422 after a trash click) stays retired.
    if (this.hasDestroyFieldTarget && this.destroyFieldTarget.value === '1') this.#retireFields();
    this.#syncKindVisibility();
    this.#recompute();
    this.#updateHeader();
    this.#updateSingleVatVisibility();
    // Initial paint: grey the derived HT field if the line opens with the
    // multi-rate split already on (persisted forfait split).
    const splitOn = this.hasSplitVatCheckboxTarget && this.splitVatCheckboxTarget.checked;
    this.#applyForfaitReadonly(splitOn && this.#currentKind() === 'forfait');
  }

  /**
   * Set the forfait "Montant HT" input read-only + greyed when its value is a
   * derived total (multi-rate split ON), or editable otherwise. readonly (not
   * disabled) so the derived value is still submitted. A small always-visible
   * helper line below the field explains the auto-computation (clearer than a
   * hover tooltip / special cursor).
   */
  #applyForfaitReadonly(on) {
    if (!this.hasForfaitInputTarget) return;
    const input = this.forfaitInputTarget;
    input.readOnly = on;
    input.classList.toggle('bg-neutral-100', on);
    input.classList.toggle('text-neutral-500', on);
    if (this.hasForfaitDerivedHintTarget) {
      this.forfaitDerivedHintTarget.classList.toggle('hidden', !on);
    }
  }

  // ── Actions ──

  kindChanged(_event) {
    const kind = this.#currentKind();
    this.#applyKind(kind);
    this.#recompute();
    this.#updateHeader();
    this.#updateSingleVatVisibility();
  }

  /**
   * Toggle the multi-rate breakdown block on/off (forfait only).
   *
   * ON  → reveal the 3 fixed-€ breakdown inputs, flip `forfaitInput` to
   *       read-only (the HT becomes the derived Σ of the breakdown), and
   *       recompute the "Total réparti" readout.
   * OFF → hide the block, restore the editable mono-rate HT input, and clear
   *       the breakdown inputs so a re-check starts blank.
   *
   * Finishes by re-evaluating the shared mono-rate VAT select visibility
   * (it must hide when split is ON, show when split is OFF).
   */
  toggleSplitVat(_event) {
    const on = this.hasSplitVatCheckboxTarget && this.splitVatCheckboxTarget.checked;

    if (this.hasBreakdownBlockTarget) {
      this.breakdownBlockTarget.classList.toggle('hidden', !on);
    }
    this.#applyForfaitReadonly(on);
    if (!on) {
      this.breakdownInputTargets.forEach((input) => {
        input.value = '';
      });
    }

    this.recomputeBreakdown();
    this.#updateSingleVatVisibility();
  }

  /**
   * Sum the 3 breakdown inputs (in integer cents → no float drift) and write
   * the total into `breakdownTotal` AND mirror it onto the read-only
   * `forfaitInput` (so the derived HT and the accordion header stay in sync).
   * Mirrors the server `MoeFeeLine#rebuild_vat_breakdown_from_inputs`
   * recompose (Σ cents / 100), which is canonical on save.
   */
  recomputeBreakdown() {
    if (!this.hasBreakdownInputTarget) return;

    const cents = this.breakdownInputTargets.reduce((sum, input) => {
      const value = this.#parseAmount(input.value);
      return value === null ? sum : sum + Math.round(value * 100);
    }, 0);
    const total = cents / 100;

    if (this.hasBreakdownTotalTarget) {
      this.breakdownTotalTarget.textContent = this.#formatCurrency(total);
    }
    // Keep the derived (read-only) HT input in sync so the header amount and
    // any server round-trip echo agree with the visible total.
    if (
      this.hasForfaitInputTarget &&
      this.hasSplitVatCheckboxTarget &&
      this.splitVatCheckboxTarget.checked
    ) {
      this.forfaitInputTarget.value = total.toFixed(2);
    }

    this.#updateHeaderAmount();
  }

  /**
   * Toggle the variable "Répartir la TVA selon les marchés du chantier"
   * checkbox. ON reveals the weighted preview block; OFF hands VAT back to the
   * shared mono-rate select. The preview ROWS are (re)computed client-side from
   * the included marchés' VAT buckets so the split is live — even for a
   * brand-new, unsaved line. The server STILL recomputes the canonical slices
   * via `MoeFee::Computer` on save.
   */
  toggleWeightChantier(_event) {
    const weighted =
      this.hasWeightChantierCheckboxTarget && this.weightChantierCheckboxTarget.checked;

    if (this.hasWeightedPreviewTarget) {
      this.weightedPreviewTarget.classList.toggle('hidden', !weighted);
    }
    if (weighted) this.#recomputeWeightedPreview();

    this.#updateSingleVatVisibility();
  }

  // Mirror the libellé input value into the accordion summary header so the
  // user sees their typed phase name without expanding/collapsing the row.
  // Falls back to "Nouvelle phase" when the input is empty (matches the
  // server-rendered placeholder in `LineRowComponent#summary_label`).
  labelChanged(event) {
    if (!this.hasHeaderLabelTarget) return;
    const value = event.target.value.trim();
    this.headerLabelTarget.textContent = value === '' ? 'Nouvelle phase' : value;
  }

  toggleContract(event) {
    this.#syncExclusionEnabled(event.target, this.contractExclusionIdTargets, 'contractId');
    this.#recompute();
  }

  toggleAdditionalWork(event) {
    this.#syncExclusionEnabled(
      event.target,
      this.additionalWorkExclusionIdTargets,
      'additionalWorkId'
    );
    this.#refreshTsCountersForContract(event.target.dataset.parentContractId);
    this.#recompute();
  }

  removeRow(event) {
    event?.preventDefault();
    // Trash sits inside <summary> — without stopPropagation the click would
    // bubble up and toggle the <details> open/closed (HTML default action on
    // any click in <summary>). preventDefault alone isn't enough because the
    // toggle is the click default, not a downstream listener.
    event?.stopPropagation();
    if (this.hasDestroyFieldTarget) {
      this.destroyFieldTarget.value = '1';
    }
    this.#retireFields();
    this.element.classList.add('hidden');
    this.dispatch('removed');
  }

  /**
   * A removed row is destroyed on save, so its fields must not block the form:
   * a hidden empty `required` libellé, or an out-of-range amount or rate, made
   * the browser refuse the submit with no visible message (the invalid control
   * is hidden). Disabled controls are barred from constraint validation and not
   * submitted, so only `_destroy` and the row's own `id` stay enabled: a new row
   * then posts `_destroy` alone (dropped by `reject_if: :all_blank`), a
   * persisted one `id` + `_destroy` (destroyed). No-op without a destroy field.
   */
  #retireFields() {
    if (!this.hasDestroyFieldTarget) return;

    const keep = this.destroyFieldTarget;
    const idName = keep.name.replace(/\[_destroy\]$/, '[id]');
    this.element.querySelectorAll('input, select, textarea').forEach((field) => {
      if (field === keep || field.name === idName) return;
      field.disabled = true;
    });
  }

  /**
   * Public action wired to `input` on the percentage / amount inputs.
   * Wraps the private `#recompute` so the action is nameable from `data-action`.
   */
  recompute(_event) {
    this.#recompute();
  }

  /**
   * Listens for the `moe-fee-global-vat:changed` window event fired by
   * MoeFeeGlobalVatController when the architect changes the global TVA
   * segmented. Updates this row's per-line hint without a server round-trip.
   */
  globalVatChanged(event) {
    if (!this.hasGlobalVatHintTarget) return;
    const rate = event.detail?.rate;
    if (rate === undefined || rate === null) return;
    const formatted = new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(rate);
    // \u00a0 (NBSP) before the % to match the server render's `&nbsp;%` so the
    // spacing doesn't visibly flip after a global-default change. Escape (not a
    // raw NBSP byte) keeps eslint no-irregular-whitespace happy.
    this.globalVatHintTarget.textContent = `TVA par défaut : ${formatted}\u00a0%`;
  }

  // ── Private ──

  /**
   * Returns the currently-selected kind value, or null if no radio is checked.
   * @returns {string|null}
   */
  #currentKind() {
    const checked = this.kindRadioTargets.find((radio) => radio.checked);
    return checked ? checked.value : null;
  }

  /**
   * Initial visibility sync on connect: respects whichever radio was
   * server-rendered as checked.
   */
  #syncKindVisibility() {
    const kind = this.#currentKind();
    if (kind) this.#applyKind(kind, { clearInactive: false });
  }

  /**
   * Apply visibility rules for a given kind, optionally clearing the
   * now-inactive numeric input (skipped on initial connect to preserve
   * server-rendered values). When triggered by a user toggle (default
   * `clearInactive: true`), also backfills the now-active input to '0'
   * if empty, so the form never submits a blank required field.
   *
   * @param {string} kind - "forfait" or "variable"
   * @param {object} [opts]
   * @param {boolean} [opts.clearInactive=true]
   */
  #applyKind(kind, { clearInactive = true } = {}) {
    const isForfait = kind === 'forfait';
    const isVariable = kind === 'variable';

    if (this.hasForfaitFieldTarget) {
      this.forfaitFieldTarget.classList.toggle('hidden', !isForfait);
    }
    if (this.hasVariableFieldTarget) {
      this.variableFieldTarget.classList.toggle('hidden', !isVariable);
    }
    if (this.hasForfaitExtrasFieldTarget) {
      this.forfaitExtrasFieldTarget.classList.toggle('hidden', !isForfait);
    }
    if (this.hasVariableExtrasFieldTarget) {
      this.variableExtrasFieldTarget.classList.toggle('hidden', !isVariable);
    }
    if (this.hasAssietteSectionTarget) {
      this.assietteSectionTarget.classList.toggle('hidden', !isVariable);
      this.assietteSectionTarget.setAttribute('aria-hidden', String(!isVariable));
    }

    if (clearInactive) {
      if (!isForfait && this.hasForfaitInputTarget) {
        this.forfaitInputTarget.value = '';
      }
      if (!isVariable && this.hasVariableInputTarget) {
        this.variableInputTarget.value = '';
      }

      // R8 — purge the multi-rate breakdown state when flipping kind so the
      // shared mono-rate VAT select stays consistent: a forfait→variable
      // switch must drop the split-VAT checkbox + breakdown inputs (the
      // server-side before_validation also purges `vat_breakdown` when the
      // line is no longer a forfait). The HT input is restored to editable so
      // a switch back to forfait starts in mono mode.
      if (this.hasSplitVatCheckboxTarget) {
        this.splitVatCheckboxTarget.checked = false;
      }
      if (this.hasBreakdownInputTarget) {
        this.breakdownInputTargets.forEach((input) => {
          input.value = '';
        });
      }
      if (this.hasBreakdownBlockTarget) {
        this.breakdownBlockTarget.classList.add('hidden');
      }
      this.#applyForfaitReadonly(false);
      this.#updateSingleVatVisibility();

      // Intentionally no '0' auto-fill on kind toggle — AR `presence: true`
      // catches blank submits, and 0 is now a valid sentinel for "phase offerte"
      // that must be typed explicitly (avoids phantom offered lines).
    }
  }

  /**
   * Sync a checkbox change with its paired hidden input's `disabled` attr.
   *
   * The hidden input carries `value="<contract.id>"` (or AW id) and is
   * `disabled` when the contract is INCLUDED — browsers omit disabled
   * controls from form submission per HTML spec, so the submitted set IS
   * the excluded set. Toggling the checkbox flips the disabled flag.
   *
   *   checkbox.checked === true   → contract is INCLUDED → hidden DISABLED
   *   checkbox.checked === false  → contract is EXCLUDED → hidden ENABLED
   *
   * Lookup pairs the checkbox to its hidden via `data-contract-id`
   * (or `data-additional-work-id`) — the same attribute set on BOTH inputs.
   *
   * @param {HTMLInputElement} checkbox
   * @param {HTMLInputElement[]} exclusionIdTargets
   * @param {string} idAttr - "contractId" or "additionalWorkId" (camelCase
   *                          dataset key matching `data-contract-id` etc.)
   */
  #syncExclusionEnabled(checkbox, exclusionIdTargets, idAttr) {
    const id = checkbox.dataset[idAttr];
    if (!id) return;

    const paired = exclusionIdTargets.find((field) => field.dataset[idAttr] === id);
    if (!paired) return;

    // Checked = INCLUDED → exclusion hidden must NOT submit → disable it.
    // Unchecked = EXCLUDED → exclusion hidden MUST submit → enable it.
    paired.disabled = checkbox.checked;
  }

  /**
   * Refresh the "X/Y TS inclus" counter on the marché accordion summary
   * when a TS checkbox inside that marché flips. Looks up the counter via
   * its `data-contract-id`, then walks the additionalWorkCheckbox targets
   * filtered by `data-parent-contract-id` to count the currently-checked
   * ones. Y (the total) is taken from the existing counter text via a
   * stable `/N TS` regex so we don't need a separate `data-total-ts`
   * attribute — the total never changes within a single edit session.
   */
  #refreshTsCountersForContract(contractId) {
    if (!contractId || !this.hasContractTsCounterTarget) return;

    const counter = this.contractTsCounterTargets.find(
      (el) => el.dataset.contractId === contractId
    );
    if (!counter) return;

    const total = this.additionalWorkCheckboxTargets.filter(
      (cb) => cb.dataset.parentContractId === contractId
    ).length;
    const included = this.additionalWorkCheckboxTargets.filter(
      (cb) => cb.dataset.parentContractId === contractId && cb.checked
    ).length;

    counter.textContent = `${included}/${total} TS inclus`;
  }

  /**
   * Show/hide the SHARED mono-rate VAT select. It is rendered once per row and
   * is only meaningful in mono mode:
   *
   *   (forfait  && !split_vat)            → mono forfait, select governs
   *   (variable && !chantier-weighting)  → mono variable, select governs
   *
   * In multi mode (forfait split ON, or variable weighting ON) the per-rate
   * breakdown / weighted preview supplies the VAT, so the select is hidden.
   * Mirrors the server-rendered visibility condition in
   * `line_row_component.html.erb` (singleVatField wrapper).
   */
  #updateSingleVatVisibility() {
    if (!this.hasSingleVatFieldTarget) return;

    const kind = this.#currentKind();
    const splitOn = this.hasSplitVatCheckboxTarget && this.splitVatCheckboxTarget.checked;
    const weightedOn =
      this.hasWeightChantierCheckboxTarget && this.weightChantierCheckboxTarget.checked;

    const mono = (kind === 'forfait' && !splitOn) || (kind === 'variable' && !weightedOn);

    this.singleVatFieldTarget.classList.toggle('hidden', !mono);
  }

  /**
   * Recompute the assiette panel's live formula + the exclusions badge from
   * current DOM state. Integer-cent math matches server BigDecimal#round(2,
   * ROUND_HALF_UP) by construction — no float drift between client preview
   * and server save. Skipped for forfait (no summary slots to update — v3
   * dropped the row-header HT readout).
   */
  #recompute() {
    const kind = this.#currentKind();
    if (kind === 'variable') {
      this.#recomputeVariable();
      this.#recomputeWeightedPreview();
    }
    this.#updateHeaderAmount();
    this.#recomputeExclusionsBadge();
  }

  /**
   * Live "Pondération chantier" preview, computed CLIENT-SIDE so it works for
   * an unsaved line and reacts instantly to %/marché changes. Mirrors
   * `MoeFee::Computer#variable_slices` + `#allocate_by_weights` in integer
   * cents (round half-away-from-zero == half-up for the non-negative amounts
   * here): total_ht = assiette × rate ; weights from the included marchés' VAT
   * buckets ; rounding residue reported onto the biggest slice so Σ == total.
   * The server recomputes canonically on save — this is display-only.
   */
  #recomputeWeightedPreview() {
    if (!this.hasWeightedPreviewRowsTarget) return;
    if (this.#currentKind() !== 'variable') return;

    const rate = this.hasVariableInputTarget
      ? this.#parseAmount(this.variableInputTarget.value) || 0
      : 0;

    let assietteCents = 0;
    const buckets = { 5.5: 0, 10.0: 0, 20.0: 0 };
    const accumulate = (cb) => {
      if (!cb.checked) return;
      assietteCents += parseInt(cb.dataset.amountHtCents || '0', 10);
      buckets[5.5] += parseInt(cb.dataset.vat55Cents || '0', 10);
      buckets[10.0] += parseInt(cb.dataset.vat10Cents || '0', 10);
      buckets[20.0] += parseInt(cb.dataset.vat20Cents || '0', 10);
    };
    if (this.hasContractCheckboxTarget) this.contractCheckboxTargets.forEach(accumulate);
    if (this.hasAdditionalWorkCheckboxTarget)
      this.additionalWorkCheckboxTargets.forEach(accumulate);

    const totalHtCents = Math.round((assietteCents * rate) / 100);
    const totalBucket = buckets[5.5] + buckets[10.0] + buckets[20.0];

    let slices;
    if (totalBucket === 0) {
      // No chantier buckets (empty assiette) → single mono slice at the applied
      // rate, mirroring the server fallback.
      slices = [{ rate: this.#appliedRateForPreview(), htCents: totalHtCents, share: 100 }];
    } else {
      slices = [5.5, 10.0, 20.0]
        .filter((r) => buckets[r] > 0)
        .map((r) => ({
          rate: r,
          htCents: Math.round((totalHtCents * buckets[r]) / totalBucket),
          share: Math.round((buckets[r] / totalBucket) * 100),
        }));
      const allocated = slices.reduce((sum, s) => sum + s.htCents, 0);
      const residue = totalHtCents - allocated;
      if (residue !== 0 && slices.length > 0) {
        const biggest = slices.reduce((a, b) => (b.htCents > a.htCents ? b : a));
        biggest.htCents += residue;
      }
    }

    this.weightedPreviewRowsTarget.innerHTML = slices
      .map((s) => {
        const tva = Math.round((s.htCents * s.rate) / 100) / 100;
        return (
          '<div class="flex items-center justify-between text-sm">' +
          `<span class="text-neutral-700">TVA ${this.#formatRate(s.rate)} %` +
          `<span class="text-xs text-neutral-500"> (${s.share} % de la base)</span></span>` +
          `<span class="tabular-nums text-neutral-900">${this.#formatCurrency(tva)}</span>` +
          '</div>'
        );
      })
      .join('');
  }

  /**
   * Best-effort applied VAT rate for the empty-assiette fallback preview:
   * the per-line mono select if set, else the form-level "TVA par défaut".
   * Display-only — the server is authoritative.
   */
  #appliedRateForPreview() {
    if (this.hasSingleVatFieldTarget) {
      const select = this.singleVatFieldTarget.querySelector('select');
      if (select && select.value) return parseFloat(select.value);
    }
    const globalChecked = document.querySelector('input[name="moe_fee[vat_rate]"]:checked');
    return globalChecked ? parseFloat(globalChecked.value) : 20;
  }

  #recomputeVariable() {
    if (!this.hasVariableInputTarget) return;

    // Sum checked contract + AW checkboxes in INTEGER cents (no float drift).
    let cents = 0;
    if (this.hasContractCheckboxTarget) {
      this.contractCheckboxTargets.forEach((cb) => {
        if (cb.checked) cents += parseInt(cb.dataset.amountHtCents || '0', 10);
      });
    }
    if (this.hasAdditionalWorkCheckboxTarget) {
      this.additionalWorkCheckboxTargets.forEach((cb) => {
        if (cb.checked) cents += parseInt(cb.dataset.amountHtCents || '0', 10);
      });
    }

    const rate = parseFloat(this.variableInputTarget.value) || 0;
    // Match server BigDecimal#round(2, ROUND_HALF_UP). Math.round() on
    // (cents × rate / 100) is half-away-from-zero on the cent value, which
    // matches half-up for non-negative amounts (rates always ≥ 0 here).
    const htCents = Math.round((cents * rate) / 100);
    const ht = htCents / 100;
    const assiette = cents / 100;

    if (this.hasSummaryAssietteTarget) {
      this.summaryAssietteTarget.textContent = this.#formatCurrency(assiette);
    }
    if (this.hasSummaryRateTarget) {
      this.summaryRateTarget.textContent = `${this.#formatRate(rate)} %`;
    }
    if (this.hasSummaryHtTarget) {
      this.summaryHtTarget.textContent = this.#formatCurrency(ht);
    }
  }

  #recomputeExclusionsBadge() {
    if (!this.hasExclusionsBadgeTarget) return;

    let count = 0;
    if (this.hasContractCheckboxTarget) {
      count += this.contractCheckboxTargets.filter((cb) => !cb.checked).length;
    }
    if (this.hasAdditionalWorkCheckboxTarget) {
      count += this.additionalWorkCheckboxTargets.filter((cb) => !cb.checked).length;
    }

    this.exclusionsBadgeTarget.classList.toggle('hidden', count === 0);

    // Update inner text via Ui::BadgeComponent's text-bearing <span>;
    // fall back to the badge root if the inner span isn't found
    // (defensive — current BadgeComponent template always wraps in <span>).
    const noun = count > 1 ? 'exclusions' : 'exclusion';
    const inner = this.exclusionsBadgeTarget.querySelector('span') || this.exclusionsBadgeTarget;
    inner.textContent = `${count} ${noun}`;
  }

  /**
   * Refresh the accordion summary header (type badge + amount). Called on
   * connect and whenever the kind radio flips. The label is handled by its
   * own `labelChanged` action because it fires on `input` per keystroke.
   */
  #updateHeader() {
    const kind = this.#currentKind();
    if (this.hasHeaderForfaitBadgeTarget) {
      this.headerForfaitBadgeTarget.classList.toggle('hidden', kind !== 'forfait');
    }
    if (this.hasHeaderVariableBadgeTarget) {
      this.headerVariableBadgeTarget.classList.toggle('hidden', kind !== 'variable');
    }
    this.#updateHeaderAmount();
  }

  /**
   * Distinguish "empty input" from "typed zero". Returns null for blank/
   * non-finite input, otherwise the parsed Number (including 0).
   * @param {string|null|undefined} raw
   * @returns {number|null}
   */
  #parseAmount(raw) {
    if (raw == null) return null;
    const trimmed = String(raw).trim();
    if (trimmed === '') return null;
    const n = parseFloat(trimmed);
    return Number.isFinite(n) ? n : null;
  }

  /**
   * Recompute the amount shown on the right of the summary header. For
   * forfait rows we mirror the `forfaitInput` value; for variable rows we
   * reuse the same integer-cent math as `#recomputeVariable`. Empty inputs
   * render an em-dash; a typed `0` renders `0,00 €` so the architect can see
   * the offered-phase intent reflected immediately (vs being masked as "—").
   */
  #updateHeaderAmount() {
    if (!this.hasHeaderAmountTarget) return;

    const kind = this.#currentKind();
    let amount = null;

    if (kind === 'forfait' && this.hasForfaitInputTarget) {
      amount = this.#parseAmount(this.forfaitInputTarget.value);
    } else if (kind === 'variable' && this.hasVariableInputTarget) {
      const rate = this.#parseAmount(this.variableInputTarget.value);
      if (rate === null) {
        amount = null;
      } else {
        let cents = 0;
        if (this.hasContractCheckboxTarget) {
          this.contractCheckboxTargets.forEach((cb) => {
            if (cb.checked) cents += parseInt(cb.dataset.amountHtCents || '0', 10);
          });
        }
        if (this.hasAdditionalWorkCheckboxTarget) {
          this.additionalWorkCheckboxTargets.forEach((cb) => {
            if (cb.checked) cents += parseInt(cb.dataset.amountHtCents || '0', 10);
          });
        }
        amount = Math.round((cents * rate) / 100) / 100;
      }
    }

    this.headerAmountTarget.textContent = amount === null ? '—' : this.#formatCurrency(amount);
  }

  /**
   * Format a Number as a French Euro string. Mirrors fr-FR Intl conventions
   * used elsewhere in the codebase (progress_calculator_controller.js).
   */
  #formatCurrency(amount) {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  }

  /**
   * Format a percentage rate using French separators (comma decimal),
   * stripping insignificant zeros.
   */
  #formatRate(rate) {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 6,
    }).format(rate);
  }
}
